/**
 * Skill profile primitives — surfaced inside the Zone-3 disclosure.
 *
 *   • skillBand(ratio)  — pure helper: maps an achieved/required ratio
 *                         to a band {key, label, tint}.
 *   • SkillRow          — single skill row with band pill + bar.
 *   • SkillDetailModal  — click-to-expand raw scores modal.
 */
import React from 'react';
import { Info, X } from 'lucide-react';
import { Perf, usePIEDisplayMode } from '../PIEDisplayMode';

/**
 * Map an achieved/required ratio to a band label + color tint. The
 * ratio is the user's achieved level divided by the level their role
 * requires — e.g. 4.6 / 4.0 = 1.15 = "exceeds".
 */
export function skillBand(ratio) {
  if (ratio == null || Number.isNaN(ratio)) return { key: 'unknown', label: '—', tint: 'muted' };
  if (ratio < 0.6) return { key: 'gap', label: 'Skill gap', tint: 'danger' };
  if (ratio < 0.9) return { key: 'developing', label: 'Developing', tint: 'warn' };
  if (ratio < 1.0) return { key: 'on_track', label: 'Nearly there', tint: 'on-track' };
  if (ratio < 1.1) return { key: 'meets', label: 'Meets level', tint: 'accent' };
  return { key: 'exceeds', label: 'Exceeds', tint: 'success' };
}

export function SkillRow({ skill, onInfo }) {
  const ratio = skill.ratio || 0;
  const band = skillBand(ratio);
  const pctNum = Math.round(ratio * 100);
  // Scale the bar so 100% sits at ~77% of the track and there's
  // visible headroom for "exceeds" cases up to 130%.
  const SCALE = 130;
  const barWidth = Math.max(0, Math.min(pctNum, SCALE)) / SCALE * 100;
  return (
    <div className={`pie-skill-row pie-skill-row-${band.tint}`} data-testid={`pie-skill-row-${skill.skill_name?.toLowerCase().replace(/\s+/g, '-')}`} data-band={band.key}>
      <div className="pie-skill-row-head">
        <span className="pie-skill-name">{skill.skill_name}</span>
        <span className="pie-skill-meta">
          <span className={`pie-skill-band-pill pie-skill-band-${band.tint}`}>{band.label}</span>
          <span className="pie-mono pie-skill-pct"><Perf value={ratio} kind="pct01" /></span>
          {onInfo && (
            <button
              type="button"
              className="pie-skill-info-btn"
              onClick={onInfo}
              aria-label={`Details for ${skill.skill_name}`}
              title="Show raw scores"
              data-testid={`pie-skill-info-${skill.skill_name?.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <Info size={11} />
            </button>
          )}
        </span>
      </div>
      <div className="pie-skill-bar">
        <div className={`pie-skill-bar-fill pie-skill-bar-fill-${band.tint}`} style={{ width: `${barWidth}%` }} />
        <div className="pie-skill-bar-target" aria-hidden title="Role requirement (100%)" />
      </div>
    </div>
  );
}

export function SkillDetailModal({ skill, onClose }) {
  const ratio = skill.ratio || 0;
  const band = skillBand(ratio);
  const { mode } = usePIEDisplayMode();
  return (
    <div className="pie-modal-backdrop" onClick={onClose} role="presentation" data-testid="pie-skill-modal">
      <div className="pie-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="pie-skill-modal-title">
        <div className="pie-modal-head">
          <h3 id="pie-skill-modal-title" className="pie-modal-title">{skill.skill_name}</h3>
          <button type="button" className="pie-modal-close" onClick={onClose} aria-label="Close" data-testid="pie-skill-modal-close">
            <X size={14} />
          </button>
        </div>
        <div className="pie-modal-body">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 12 }}>
            <span className="pie-display-md"><Perf value={ratio} kind="pct01" /></span>
            <span className={`pie-skill-band-pill pie-skill-band-${band.tint}`}>{band.label}</span>
          </div>
          {mode !== 'verbiage' && (
            <>
              <div className="pie-modal-detail-row">
                <span className="pie-modal-detail-label">Your assessed level</span>
                <span className="pie-mono pie-modal-detail-value">{skill.achieved_level?.toFixed(1)} / 5.0</span>
              </div>
              <div className="pie-modal-detail-row">
                <span className="pie-modal-detail-label">Level your role requires</span>
                <span className="pie-mono pie-modal-detail-value">{skill.required_level?.toFixed(1)} / 5.0</span>
              </div>
            </>
          )}
          <p className="pie-modal-foot">
            {mode === 'verbiage'
              ? 'Each band reflects where you sit against the level your role requires.'
              : 'Levels are scored on a 1–5 scale. The percentage is your assessed level divided by the level your role requires — so anything ≥100% means you\u2019re at or above the bar.'}
          </p>
        </div>
      </div>
    </div>
  );
}
